import {PedagogicalLevelFields} from './PedagogicalLevelFields';
import { useMemo, useRef, useState } from "react";
import { Camera, DoorOpen, FileSpreadsheet, FolderOpen, Images, Pencil, Plus, Save, School, Trash2, Upload, UserPlus, Users } from "lucide-react";
import { SchoolLevel, subjectForSchoolLevel } from "./catalog";

export type StudentSeed = {
  id: string;
  firstName: string;
  lastName: string;
  sex: "homme" | "femme";
  officialGroup?: "A" | "B";
  photoUrl?: string;
};

export type SchoolClass = {
  id: string;
  name: string;
  schoolLevel: SchoolLevel;
  subject: string;
  students: StudentSeed[];
  roomIds?: string[];
};

export type RoomDefinition = {
  id: string;
  name: string;
};

type Props = {
  mode?: "classes" | "rooms";
  classes: SchoolClass[];
  visibleLevels: SchoolLevel[];
  activeClassId: string;
  rooms: RoomDefinition[];
  activeRoomId: string;
  onSave: (schoolClass: SchoolClass) => void;
  onUpdate: (schoolClass: SchoolClass) => void;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onAddRoom: (name: string) => void;
  onOpenRoom: (id: string) => void;
  onDeleteRoom: (id: string) => void;
};

const cleanHeader = (value: string) => value.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const newId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
const cleanIdentity = (value: string) => value.toLocaleLowerCase("fr").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");

function defaultAvatar(student: StudentSeed) {
  const hash = [...student.id].reduce((total, character) => total + character.charCodeAt(0), 0);
  const number = String((hash % 6) + 1).padStart(2, "0");
  return `/avatars/${student.sex}/${student.sex}-${number}.webp`;
}

async function compressPhoto(file: File) {
  if (!file.type.startsWith("image/")) throw new Error(`${file.name} n’est pas une image.`);
  if (file.size > 12_000_000) throw new Error(`${file.name} dépasse 12 Mo.`);
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error(`Impossible de lire ${file.name}.`));
      element.src = objectUrl;
    });
    const targetWidth = 300;
    const targetHeight = 375;
    const scale = Math.max(targetWidth / image.naturalWidth, targetHeight / image.naturalHeight);
    const sourceWidth = targetWidth / scale;
    const sourceHeight = targetHeight / scale;
    const sourceX = (image.naturalWidth - sourceWidth) / 2;
    const sourceY = (image.naturalHeight - sourceHeight) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    canvas.getContext("2d")?.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, targetWidth, targetHeight);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/webp", .8));
    if (!blob) throw new Error(`Impossible de convertir ${file.name}.`);
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error(`Impossible de préparer ${file.name}.`));
      reader.readAsDataURL(blob);
    });
  } finally { URL.revokeObjectURL(objectUrl); }
}

async function sendPhoto(classId: string, studentId: string, file: File) {
  const response = await fetch(`/api/photos/${encodeURIComponent(classId)}/${encodeURIComponent(studentId)}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ image: await compressPhoto(file) }),
  });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error ?? "Import impossible");
  return String(body.photoUrl);
}

function matchPhoto(file: File, students: StudentSeed[]) {
  const stem = cleanIdentity(file.name.replace(/\.[^.]+$/, ""));
  const exact = students.filter((student) => {
    const firstLast = cleanIdentity(`${student.firstName}${student.lastName}`);
    const lastFirst = cleanIdentity(`${student.lastName}${student.firstName}`);
    return stem === firstLast || stem === lastFirst || stem === cleanIdentity(student.id);
  });
  if (exact.length === 1) return exact[0];
  const included = students.filter((student) => {
    const first = cleanIdentity(student.firstName);
    const last = cleanIdentity(student.lastName);
    return first.length > 1 && last.length > 1 && stem.includes(first) && stem.includes(last);
  });
  return included.length === 1 ? included[0] : null;
}

function splitLine(line: string, separator: string) {
  const cells: string[] = [];
  let current = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"') {
      if (quoted && line[index + 1] === '"') { current += '"'; index += 1; }
      else quoted = !quoted;
    } else if (character === separator && !quoted) {
      cells.push(current.trim());
      current = "";
    } else current += character;
  }
  cells.push(current.trim());
  return cells;
}

function splitStudentName(value: string) {
  const parts = value.trim().replace(/\s+/g, " ").split(" ").filter(Boolean);
  const firstGivenName = parts.findIndex((part) => {
    const letters = part.replace(/[^\p{L}]/gu, "");
    return letters.length > 0 && letters !== letters.toLocaleUpperCase("fr");
  });
  if (firstGivenName > 0) {
    return { lastName: parts.slice(0, firstGivenName).join(" "), firstName: parts.slice(firstGivenName).join(" ") };
  }
  if (parts.length > 1) {
    return { lastName: parts.slice(0, -1).join(" "), firstName: parts.at(-1) ?? "" };
  }
  return { lastName: parts[0] ?? "", firstName: "" };
}

function parseStudents(text: string, classId: string): StudentSeed[] {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((line) => line.trim());
  if (lines.length === 0) return [];
  const counts = [{ separator: ";", count: (lines[0].match(/;/g) ?? []).length }, { separator: ",", count: (lines[0].match(/,/g) ?? []).length }, { separator: "\t", count: (lines[0].match(/\t/g) ?? []).length }];
  const separator = counts.sort((a, b) => b.count - a.count)[0].separator;
  const first = splitLine(lines[0], separator).map(cleanHeader);
  const hasHeader = first.some((value) => ["eleves", "eleve", "nom", "prenom", "sexe", "groupe"].includes(value));
  const headers = hasHeader ? first : ["nom", "prenom", "sexe", "groupe"];
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const indexOf = (...names: string[]) => headers.findIndex((header) => names.includes(header));
  const lastNameIndex = indexOf("nom", "lastname", "nom de famille");
  const firstNameIndex = indexOf("prenom", "firstname");
  const sexIndex = indexOf("sexe", "genre");
  const groupIndex = indexOf("groupe", "group", "groupe officiel");
  const fullNameIndex = indexOf("eleves", "eleve", "nom complet", "nom et prenom");

  return dataLines.map((line, index) => {
    const cells = splitLine(line, separator);
    const splitName = fullNameIndex >= 0 ? splitStudentName(cells[fullNameIndex] ?? "") : null;
    const lastName = splitName?.lastName ?? cells[lastNameIndex >= 0 ? lastNameIndex : 0]?.trim() ?? "";
    const firstName = splitName?.firstName ?? cells[firstNameIndex >= 0 ? firstNameIndex : 1]?.trim() ?? "";
    const rawSex = cleanHeader(cells[sexIndex] ?? "");
    const rawGroup = (cells[groupIndex] ?? "").trim().toUpperCase();
    const sex: StudentSeed["sex"] = ["f", "femme", "fille", "female", "feminin"].includes(rawSex) ? "femme" : "homme";
    const officialGroup: StudentSeed["officialGroup"] = rawGroup === "A" || rawGroup === "B" ? rawGroup : undefined;
    return {
      id: `${classId}-student-${index + 1}`,
      firstName,
      lastName,
      sex,
      officialGroup,
    };
  }).filter((student) => student.firstName || student.lastName);
}

export function ClassesView({ mode, classes, visibleLevels, activeClassId, rooms, activeRoomId, onSave, onUpdate, onOpen, onDelete, onAddRoom, onOpenRoom, onDeleteRoom }: Props) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [level, setLevel] = useState<SchoolLevel>(visibleLevels[0] ?? "Seconde");
  const [students, setStudents] = useState<StudentSeed[]>([]);
  const [fileName, setFileName] = useState("");
  const [editingGroupsId, setEditingGroupsId] = useState<string | null>(null);
  const [editingDetailsId, setEditingDetailsId] = useState<string | null>(null);
  const [classNameDraft, setClassNameDraft] = useState("");
  const [classLevelDraft, setClassLevelDraft] = useState<SchoolLevel>(visibleLevels[0] ?? "Seconde");
  const [editingStudentsId, setEditingStudentsId] = useState<string | null>(null);
  const [studentDrafts, setStudentDrafts] = useState<StudentSeed[]>([]);
  const [editingRoomsId, setEditingRoomsId] = useState<string | null>(null);
  const [editingPhotosId, setEditingPhotosId] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoNotice, setPhotoNotice] = useState("");
  const [photoError, setPhotoError] = useState("");
  const [roomName, setRoomName] = useState("");
  const [selectedRoomIds, setSelectedRoomIds] = useState<string[]>(rooms.map((room) => room.id));
  const classId = useMemo(() => newId("class"), [showForm]);
  const fileInput = useRef<HTMLInputElement>(null);

  async function importFile(file?: File) {
    if (!file) return;
    setFileName(file.name);
    setStudents(parseStudents(await file.text(), classId));
  }

  async function importPhotos(schoolClass: SchoolClass, files: FileList | null) {
    if (!files?.length || photoBusy) return;
    setPhotoBusy(true); setPhotoError(""); setPhotoNotice("");
    const matched = [...files].map((file) => ({ file, student: matchPhoto(file, schoolClass.students) })).filter((item) => item.student) as { file: File; student: StudentSeed }[];
    const unmatched = [...files].filter((file) => !matched.some((item) => item.file === file));
    try {
      const urls = new Map<string, string>();
      for (const item of matched) urls.set(item.student.id, await sendPhoto(schoolClass.id, item.student.id, item.file));
      if (urls.size) onUpdate({ ...schoolClass, students: schoolClass.students.map((student) => urls.has(student.id) ? { ...student, photoUrl: urls.get(student.id) } : student) });
      setPhotoNotice(`${urls.size} photo${urls.size > 1 ? "s" : ""} associée${urls.size > 1 ? "s" : ""}.${unmatched.length ? ` ${unmatched.length} fichier${unmatched.length > 1 ? "s" : ""} non reconnu${unmatched.length > 1 ? "s" : ""} : ${unmatched.map((file) => file.name).join(", ")}.` : ""}`);
    } catch (reason) { setPhotoError(reason instanceof Error ? reason.message : "Import impossible"); }
    finally { setPhotoBusy(false); }
  }

  async function importStudentPhoto(schoolClass: SchoolClass, student: StudentSeed, file?: File) {
    if (!file || photoBusy) return;
    setPhotoBusy(true); setPhotoError(""); setPhotoNotice("");
    try {
      const photoUrl = await sendPhoto(schoolClass.id, student.id, file);
      onUpdate({ ...schoolClass, students: schoolClass.students.map((item) => item.id === student.id ? { ...item, photoUrl } : item) });
      setPhotoNotice(`Photo de ${student.firstName} ${student.lastName} enregistrée.`);
    } catch (reason) { setPhotoError(reason instanceof Error ? reason.message : "Import impossible"); }
    finally { setPhotoBusy(false); }
  }

  async function deleteStudentPhoto(schoolClass: SchoolClass, student: StudentSeed) {
    if (!student.photoUrl || photoBusy) return;
    setPhotoBusy(true); setPhotoError(""); setPhotoNotice("");
    try {
      const response = await fetch(`/api/photos/${encodeURIComponent(schoolClass.id)}/${encodeURIComponent(student.id)}`, { method: "DELETE", credentials: "include" });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error ?? "Suppression impossible");
      onUpdate({ ...schoolClass, students: schoolClass.students.map((item) => item.id === student.id ? { ...item, photoUrl: undefined } : item) });
      setPhotoNotice(`Photo de ${student.firstName} ${student.lastName} supprimée.`);
    } catch (reason) { setPhotoError(reason instanceof Error ? reason.message : "Suppression impossible"); }
    finally { setPhotoBusy(false); }
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    onSave({ id: classId, name: name.trim(), schoolLevel: level, subject: subjectForSchoolLevel(level), students, roomIds: selectedRoomIds });
    setName("");
    setStudents([]);
    setFileName("");
    setShowForm(false);
  }

  function updateOfficialGroup(schoolClass: SchoolClass, studentId: string, officialGroup?: "A" | "B") {
    onUpdate({
      ...schoolClass,
      students: schoolClass.students.map((student) => student.id === studentId ? { ...student, officialGroup } : student),
    });
  }

  function toggleStudentEditor(schoolClass: SchoolClass) {
    if (editingStudentsId === schoolClass.id) {
      setEditingStudentsId(null);
      setStudentDrafts([]);
      return;
    }
    setStudentDrafts(schoolClass.students.map((student) => ({ ...student })));
    setEditingStudentsId(schoolClass.id);
    setEditingGroupsId(null);
    setEditingRoomsId(null);
    setEditingPhotosId(null);
  }

  function toggleClassDetails(schoolClass: SchoolClass) {
    if (editingDetailsId === schoolClass.id) {
      setEditingDetailsId(null);
      return;
    }
    setClassNameDraft(schoolClass.name);
    setClassLevelDraft(schoolClass.schoolLevel);
    setEditingDetailsId(schoolClass.id);
    setEditingStudentsId(null);
    setEditingGroupsId(null);
    setEditingRoomsId(null);
    setEditingPhotosId(null);
  }

  function saveClassDetails(schoolClass: SchoolClass) {
    const nextName = classNameDraft.trim();
    if (!nextName) return;
    onUpdate({ ...schoolClass, name: nextName, schoolLevel: classLevelDraft, subject: classLevelDraft === schoolClass.schoolLevel ? schoolClass.subject : subjectForSchoolLevel(classLevelDraft) });
    setEditingDetailsId(null);
  }

  function updateStudentDraft(id: string, changes: Partial<StudentSeed>) {
    setStudentDrafts((current) => current.map((student) => student.id === id ? { ...student, ...changes } : student));
  }

  function addStudentDraft(schoolClass: SchoolClass) {
    setStudentDrafts((current) => [...current, {
      id: newId(`${schoolClass.id}-student`),
      firstName: "",
      lastName: "",
      sex: "homme",
    }]);
  }

  function removeStudentDraft(student: StudentSeed) {
    if ((student.firstName || student.lastName) && !window.confirm(`Supprimer ${student.firstName} ${student.lastName} de cette classe ?\n\nSes résultats, sa photo et son éventuel compte élève seront supprimés.`)) return;
    setStudentDrafts((current) => current.filter((item) => item.id !== student.id));
  }

  function saveStudentDrafts(schoolClass: SchoolClass) {
    const cleaned = studentDrafts.map((student) => ({
      ...student,
      firstName: student.firstName.trim(),
      lastName: student.lastName.trim(),
    }));
    if (cleaned.some((student) => !student.firstName || !student.lastName)) return;
    onUpdate({ ...schoolClass, students: cleaned });
    setEditingStudentsId(null);
    setStudentDrafts([]);
  }

  function toggleClassRoom(schoolClass: SchoolClass, roomId: string) {
    const currentRoomIds = schoolClass.roomIds ?? rooms.map((room) => room.id);
    const nextRoomIds = currentRoomIds.includes(roomId) ? currentRoomIds.filter((id) => id !== roomId) : [...currentRoomIds, roomId];
    if (nextRoomIds.length === 0) return;
    onUpdate({ ...schoolClass, roomIds: nextRoomIds });
  }

  function toggleNewClassRoom(roomId: string) {
    setSelectedRoomIds((current) => current.includes(roomId)
      ? current.length === 1 ? current : current.filter((id) => id !== roomId)
      : [...current, roomId]);
  }

  function submitRoom(event: React.FormEvent) {
    event.preventDefault();
    if (!roomName.trim()) return;
    onAddRoom(roomName.trim());
    setRoomName("");
  }

  return (
    <main className="management-page">
      {mode !== "rooms" && <>
      <div className="management-heading">
        <div><span className="eyebrow">Organisation</span><h1>Mes classes</h1></div>
        <button className="primary" onClick={() => { setSelectedRoomIds(rooms.map((room) => room.id)); setShowForm(true); }}><Plus size={17} /> Nouvelle classe</button>
      </div>

      {showForm && <form className="class-form" onSubmit={submit}>
        <div className="form-title"><strong>Créer une classe</strong><button type="button" onClick={() => setShowForm(false)}>Annuler</button></div>
        <label><span>Nom de la classe</span><input value={name} onChange={(event) => setName(event.target.value)} placeholder="Ex. Terminale 2" required /></label>
        <PedagogicalLevelFields value={level} levels={visibleLevels} onChange={value=>setLevel(value as SchoolLevel)} />
        <fieldset className="class-room-picker"><legend>Salles utilisées par cette classe</legend>{rooms.map((room) => <label key={room.id}><input type="checkbox" checked={selectedRoomIds.includes(room.id)} onChange={() => toggleNewClassRoom(room.id)} /><span>{room.name}</span></label>)}</fieldset>
        <div className="csv-import">
          <input ref={fileInput} type="file" accept=".csv,text/csv,.txt" onChange={(event) => importFile(event.target.files?.[0])} />
          <button type="button" className="csv-drop" onClick={() => fileInput.current?.click()}><Upload size={20} /><span>{fileName || "Importer les élèves en CSV"}</span><small>Compatible avec ton export « Élèves ; Sexe ; … »</small></button>
          {students.length > 0 && <strong className="import-count"><Users size={16} /> {students.length} élève{students.length > 1 ? "s" : ""} détecté{students.length > 1 ? "s" : ""}</strong>}
          {students.length > 0 && <div className="student-import-preview">{students.slice(0, 5).map((student) => <span key={student.id}>{student.lastName} <strong>{student.firstName}</strong> · {student.sex === "femme" ? "F" : "M"}</span>)}{students.length > 5 && <small>+ {students.length - 5} autres élèves</small>}</div>}
        </div>
        <button className="primary class-save" type="submit"><School size={17} /> Créer la classe</button>
      </form>}

      <div className="classes-grid">
        {classes.filter((item) => visibleLevels.includes(item.schoolLevel)).map((schoolClass) => {
          const groupA = schoolClass.students.filter((student) => student.officialGroup === "A").length;
          const groupB = schoolClass.students.filter((student) => student.officialGroup === "B").length;
          const classRoomIds = schoolClass.roomIds ?? rooms.map((room) => room.id);
          return <article className={`class-card ${schoolClass.id === activeClassId ? "active" : ""}`} key={schoolClass.id}>
            <div className="class-card-icon"><School size={22} /></div>
            <div className="class-card-copy"><span>{schoolClass.schoolLevel}</span><strong>{schoolClass.name}</strong><p>{schoolClass.students.length} élèves · {classRoomIds.length} salle{classRoomIds.length > 1 ? "s" : ""}{groupA || groupB ? ` · A : ${groupA} · B : ${groupB}` : ""}</p></div>
            <div className="class-card-actions"><button className="secondary" onClick={() => toggleClassDetails(schoolClass)}><Pencil size={16} /> Modifier</button><button className="secondary" onClick={() => toggleStudentEditor(schoolClass)}><Users size={16} /> Élèves</button><button className="secondary" onClick={() => setEditingRoomsId((current) => current === schoolClass.id ? null : schoolClass.id)}><DoorOpen size={16} /> Salles</button><button className="secondary" onClick={() => setEditingGroupsId((current) => current === schoolClass.id ? null : schoolClass.id)}><Users size={16} /> Groupes A/B</button><button className="secondary" onClick={() => { setEditingPhotosId((current) => current === schoolClass.id ? null : schoolClass.id); setPhotoNotice(""); setPhotoError(""); }}><Images size={16} /> Photos</button><button className="secondary" onClick={() => onOpen(schoolClass.id)}><FolderOpen size={16} /> Ouvrir</button><button className="delete-class" disabled={classes.length === 1} onClick={() => onDelete(schoolClass.id)} aria-label={`Supprimer ${schoolClass.name}`} title={classes.length === 1 ? "Crée une autre classe avant de supprimer celle-ci" : `Supprimer ${schoolClass.name}`}><Trash2 size={16} /></button></div>
            {editingDetailsId === schoolClass.id && <div className="class-details-editor">
              <label><span>Nom de la classe</span><input value={classNameDraft} onChange={(event) => setClassNameDraft(event.target.value)} placeholder="Ex. Terminale 2" /></label>
              <PedagogicalLevelFields value={classLevelDraft} levels={visibleLevels} onChange={value=>setClassLevelDraft(value as SchoolLevel)} />
              <div><button className="secondary" onClick={() => setEditingDetailsId(null)}>Annuler</button><button className="primary compact" disabled={!classNameDraft.trim()} onClick={() => saveClassDetails(schoolClass)}><Save size={16} /> Enregistrer</button></div>
            </div>}
            {editingStudentsId === schoolClass.id && <div className="student-editor">
              <div className="student-editor-head"><div><strong>Élèves de la classe</strong><span>Modifie les informations ou ajoute un élève, puis enregistre.</span></div><button className="secondary" onClick={() => addStudentDraft(schoolClass)}><UserPlus size={16} /> Ajouter un élève</button></div>
              <div className="student-editor-list">{studentDrafts.map((student) => <div className="student-editor-row" key={student.id}>
                <input value={student.lastName} onChange={(event) => updateStudentDraft(student.id, { lastName: event.target.value })} placeholder="Nom" aria-label="Nom" />
                <input value={student.firstName} onChange={(event) => updateStudentDraft(student.id, { firstName: event.target.value })} placeholder="Prénom" aria-label="Prénom" />
                <select value={student.sex} onChange={(event) => updateStudentDraft(student.id, { sex: event.target.value as StudentSeed["sex"] })} aria-label="Sexe"><option value="homme">Garçon</option><option value="femme">Fille</option></select>
                <select value={student.officialGroup ?? ""} onChange={(event) => updateStudentDraft(student.id, { officialGroup: event.target.value ? event.target.value as "A" | "B" : undefined })} aria-label="Groupe officiel"><option value="">Classe entière</option><option value="A">Groupe A</option><option value="B">Groupe B</option></select>
                <button className="delete-student-row" onClick={() => removeStudentDraft(student)} aria-label={`Supprimer ${student.firstName} ${student.lastName}`}><Trash2 size={16} /></button>
              </div>)}</div>
              {studentDrafts.length === 0 && <div className="management-empty">Aucun élève. Utilise « Ajouter un élève ».</div>}
              <div className="student-editor-actions"><button className="secondary" onClick={() => { setEditingStudentsId(null); setStudentDrafts([]); }}>Annuler</button><button className="primary compact" disabled={studentDrafts.some((student) => !student.firstName.trim() || !student.lastName.trim())} onClick={() => saveStudentDrafts(schoolClass)}><Save size={16} /> Enregistrer les élèves</button></div>
            </div>}
            {editingRoomsId === schoolClass.id && <div className="class-room-assignment"><strong>Salles utilisées</strong><div>{rooms.map((room) => <label key={room.id}><input type="checkbox" checked={classRoomIds.includes(room.id)} onChange={() => toggleClassRoom(schoolClass, room.id)} /><span>{room.name}</span></label>)}</div><small>Au moins une salle doit rester sélectionnée.</small></div>}
            {editingGroupsId === schoolClass.id && <div className="group-assignment">
              <div><strong>Répartition des élèves</strong><span>Chaque élève peut rester en classe entière ou appartenir au groupe A ou B.</span></div>
              <div className="assignment-list">{schoolClass.students.map((student) => <label key={student.id}><span><strong>{student.lastName}</strong> {student.firstName}</span><select value={student.officialGroup ?? ""} onChange={(event) => updateOfficialGroup(schoolClass, student.id, event.target.value ? event.target.value as "A" | "B" : undefined)}><option value="">Non affecté</option><option value="A">Groupe A</option><option value="B">Groupe B</option></select></label>)}</div>
            </div>}
            {editingPhotosId === schoolClass.id && <div className="photo-assignment">
              <div className="photo-assignment-head"><div><strong>Photos des élèves</strong><span>Sélectionne plusieurs fichiers nommés NOM_PRENOM. Les autres pourront être ajoutés individuellement.</span></div><label className="primary compact photo-bulk-button"><Upload size={17} />{photoBusy ? "Import…" : "Importer plusieurs photos"}<input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={photoBusy} onChange={(event) => { void importPhotos(schoolClass, event.target.files); event.target.value = ""; }} /></label></div>
              {photoNotice && <div className="photo-notice success">{photoNotice}</div>}
              {photoError && <div className="photo-notice error">{photoError}</div>}
              <div className="photo-student-grid">{schoolClass.students.map((student) => <article key={student.id}><img src={student.photoUrl || defaultAvatar(student)} alt="" /><div><strong>{student.lastName}</strong><span>{student.firstName}</span></div><label className="secondary photo-single-button"><Camera size={15} />{student.photoUrl ? "Remplacer" : "Ajouter"}<input type="file" accept="image/jpeg,image/png,image/webp" disabled={photoBusy} onChange={(event) => { void importStudentPhoto(schoolClass, student, event.target.files?.[0]); event.target.value = ""; }} /></label>{student.photoUrl && <button className="delete-photo" disabled={photoBusy} onClick={() => void deleteStudentPhoto(schoolClass, student)} aria-label={`Supprimer la photo de ${student.firstName}`}><Trash2 size={15} /></button>}</article>)}</div>
            </div>}
          </article>;
        })}
      </div>

      <div className="csv-help"><FileSpreadsheet size={18} /><div><strong>Ton export est reconnu</strong><span>La colonne « Élèves » est séparée automatiquement en nom et prénom(s), et « Féminin/Masculin » choisit l’avatar correspondant.</span></div></div>

      </>}
      {mode !== "classes" && <section className="rooms-section">
        <div className="management-heading"><div><span className="eyebrow">Plans de classe</span><h2>Mes salles</h2></div></div>
        <form className="room-form" onSubmit={submitRoom}><input value={roomName} onChange={(event) => setRoomName(event.target.value)} placeholder="Ex. Labo de physique" aria-label="Nom de la salle" /><button className="primary" type="submit"><Plus size={17} /> Ajouter la salle</button></form>
        <div className="rooms-grid">{rooms.map((room) => <article className={`room-card ${room.id === activeRoomId ? "active" : ""}`} key={room.id}><div className="room-card-icon"><DoorOpen size={21} /></div><strong>{room.name}</strong><span>Grille de 5 × 8</span><button className="secondary" onClick={() => onOpenRoom(room.id)}>Utiliser</button><button className="delete-class" disabled={rooms.length === 1} onClick={() => onDeleteRoom(room.id)} aria-label={`Supprimer ${room.name}`} title={rooms.length === 1 ? "Crée une autre salle avant de supprimer celle-ci" : `Supprimer ${room.name}`}><Trash2 size={16} /></button></article>)}</div>
      </section>}
    </main>
  );
}
